import React, { useState } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { DollarSign, Percent, Calendar, RefreshCw, Calculator as CalcIcon, TrendingUp, CreditCard } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';

const CarLoanInterestCalculator = () => {
  // Calculator 1: Total Interest Paid
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [extraPayment, setExtraPayment] = useState('0');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');

  // Calculator 2: Compare Interest Rates
  const [compareLoanAmount, setCompareLoanAmount] = useState('');
  const [compareLoanTerm, setCompareLoanTerm] = useState('');
  const [interestRate1, setInterestRate1] = useState('');
  const [interestRate2, setInterestRate2] = useState('');
  const [interestRate3, setInterestRate3] = useState('');

  // Results
  const [interestResult, setInterestResult] = useState<{
    totalInterest: number;
    totalInterestWithExtra: number;
    interestSaved: number;
    monthlyPayment: number;
    interestPercentage: number;
    principalPercentage: number;
    earlyPayoffMonths: number;
  } | null>(null);

  const [compareResult, setCompareResult] = useState<{
    comparisons: Array<{
      rate: number;
      monthlyPayment: number;
      totalInterest: number;
      totalCost: number;
    }>;
  } | null>(null);

  // Calculate total interest
  const calculateInterest = () => {
    // Convert inputs to numbers
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate) / 100 / 12; // Monthly interest rate
    const term = parseFloat(loanTerm) * 12; // Term in months
    const extra = parseFloat(extraPayment || '0');

    if (principal > 0 && rate > 0 && term > 0) {
      // Standard monthly payment formula: P × (r × (1 + r)^n) / ((1 + r)^n - 1)
      const monthlyPayment = principal * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
      
      // Calculate total interest without extra payments
      const totalInterest = (monthlyPayment * term) - principal;
      
      // Calculate total interest with extra payments
      let balance = principal;
      let month = 0;
      let totalInterestWithExtra = 0;
      
      while (balance > 0 && month < term) {
        month++;
        const interestForMonth = balance * rate;
        totalInterestWithExtra += interestForMonth;
        
        // Monthly payment + extra payment
        const payment = Math.min(monthlyPayment + extra, balance + interestForMonth);
        balance = balance - (payment - interestForMonth);
      }
      
      const interestSaved = totalInterest - totalInterestWithExtra;
      const earlyPayoffMonths = term - month;
      
      // Calculate percentage breakdown
      const interestPercentage = (totalInterest / (principal + totalInterest)) * 100;
      const principalPercentage = 100 - interestPercentage;
      
      setInterestResult({
        totalInterest,
        totalInterestWithExtra,
        interestSaved,
        monthlyPayment,
        interestPercentage,
        principalPercentage,
        earlyPayoffMonths
      });
    }
  };

  // Compare different interest rates
  const compareInterestRates = () => {
    const principal = parseFloat(compareLoanAmount);
    const term = parseFloat(compareLoanTerm) * 12; // Term in months
    
    const rates = [
      parseFloat(interestRate1),
      parseFloat(interestRate2),
      parseFloat(interestRate3)
    ].filter(rate => !isNaN(rate) && rate > 0);
    
    if (principal > 0 && term > 0 && rates.length > 0) {
      const comparisons = rates.map(interestRate => {
        const monthlyRate = interestRate / 100 / 12;
        const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
        const totalInterest = (monthlyPayment * term) - principal;
        
        return {
          rate: interestRate,
          monthlyPayment,
          totalInterest,
          totalCost: principal + totalInterest
        };
      });
      
      setCompareResult({ comparisons });
    }
  };

  // Transform compare results into chart data
  const getChartData = () => {
    if (!compareResult) return [];
    
    return compareResult.comparisons.map(item => ({
      name: `${item.rate}%`,
      interest: item.totalInterest,
      payment: item.monthlyPayment,
      totalCost: item.totalCost
    }));
  };

  // Calculate additional interest metrics
  const getDailyInterest = () => {
    if (!interestResult) return null;
    
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate) / 100;
    
    // Daily interest in the first month
    return (principal * rate / 365).toFixed(2);
  };

  return (
    <>
      <SEOHead
        title="Car Loan Interest Calculator Australia | Calculate Total Interest & Compare Rates"
        description="Use our comprehensive Australian car loan interest calculator to calculate total interest paid, compare different rates, estimate savings from extra repayments, and find the best car financing deal."
        keywords="car loan interest calculator australia, car finance interest calculator, compare car loan interest rates, car loan comparison calculator, interest savings calculator, extra repayment calculator, car finance australia, total interest paid car loan, car loan interest estimator, car loan optimization, 2025 car loan rates"
      />
      <Helmet>
        <title>Car Loan Interest Calculator Australia | Calculate Total Interest & Compare Rates</title>
        <meta name="description" content="Use our comprehensive Australian car loan interest calculator to calculate total interest paid, compare different rates, estimate savings from extra repayments, and find the best car financing deal." />
        <meta name="keywords" content="car loan interest calculator australia, car finance interest calculator, compare car loan interest rates, car loan comparison calculator, interest savings calculator, extra repayment calculator, car finance australia, total interest paid car loan, car loan interest estimator, car loan optimization, 2025 car loan rates" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan Interest Calculator Australia",
              "description": "Calculate the total interest paid on your car loan and compare different interest rates to find the best deal in Australia for 2025.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active",
              "financialProductCategory": "LoanOrCredit"
            }
          `}
        </script>
      </Helmet>
      
      <div className="space-y-10">
        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Comprehensive Car Loan Interest Calculator Australia</h1>
          <div className="text-gray-600">
            <p className="mb-4">
              Our all-in-one car loan interest calculator helps you understand the true cost of your auto financing in Australia.
              Visualize how much interest you'll pay over the life of your loan, see how extra repayments can save you money,
              and compare different interest rates to find the best deal.
            </p>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <h2 className="text-lg font-semibold text-blue-800 mb-2">What This Calculator Does:</h2>
              <ul className="list-disc pl-5 space-y-1 text-blue-700">
                <li>Calculates your total interest payments over the loan term</li>
                <li>Shows how much you can save with extra repayments</li>
                <li>Compares up to three different interest rates side-by-side</li>
                <li>Provides daily interest insights and principal/interest breakdown</li>
                <li>Visualizes loan costs with interactive charts and tables</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Total Interest Calculator */}
        <Calculator
          title="Car Loan Interest Calculator - Total Interest Analysis"
          description="Calculate the total interest you'll pay over the life of your car loan and see how extra payments can save you money."
          icon={Percent}
          result={interestResult ? `Total Interest: $${interestResult.totalInterest.toFixed(2)}` : undefined}
        >
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Loan Amount"
                value={loanAmount}
                onChange={setLoanAmount}
                unit="$"
                placeholder="e.g. 30000"
              />
              <Input
                label="Interest Rate"
                value={interestRate}
                onChange={setInterestRate}
                unit="%"
                placeholder="e.g. 5.25"
              />
              <Input
                label="Loan Term"
                value={loanTerm}
                onChange={setLoanTerm}
                unit="years"
                placeholder="e.g. 5"
              />
              <Input
                label="Extra Monthly Payment (Optional)"
                value={extraPayment}
                onChange={setExtraPayment}
                unit="$"
                placeholder="e.g. 100"
              />
            </div>

            <button
              onClick={calculateInterest}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
            >
              Calculate Total Interest & Payments
            </button>

            {interestResult !== null && (
              <div className="mt-6 space-y-6">
                <div className="p-4 bg-blue-50 rounded-md">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Interest Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Monthly Payment</p>
                      <p className="text-xl font-bold text-blue-800">${interestResult.monthlyPayment.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Total Interest</p>
                      <p className="text-xl font-bold text-blue-800">${interestResult.totalInterest.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Interest as % of Loan</p>
                      <p className="text-xl font-bold text-blue-800">{interestResult.interestPercentage.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
                
                {parseFloat(extraPayment) > 0 && (
                  <div className="p-4 bg-green-50 rounded-md">
                    <h3 className="text-lg font-semibold text-green-800 mb-4">Extra Payment Benefits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-md shadow-sm">
                        <p className="text-sm text-gray-600">Interest Saved</p>
                        <p className="text-xl font-bold text-green-700">${interestResult.interestSaved.toFixed(2)}</p>
                      </div>
                      <div className="p-4 bg-white rounded-md shadow-sm">
                        <p className="text-sm text-gray-600">Months Paid Off Early</p>
                        <p className="text-xl font-bold text-green-700">{interestResult.earlyPayoffMonths}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-4 bg-gray-50 rounded-md">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Interest Insights</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                      <span className="text-sm text-gray-600">Daily Interest (starting):</span>
                      <span className="font-medium">${getDailyInterest()}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                      <span className="text-sm text-gray-600">Principal vs Interest Ratio:</span>
                      <span className="font-medium">{interestResult.principalPercentage.toFixed(1)}% / {interestResult.interestPercentage.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Calculator>

        {/* Transition section between calculators */}
        {interestResult !== null && (
          <div className="bg-gradient-to-r from-blue-50 to-green-50 p-5 rounded-lg border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Ready to Compare Interest Rates?</h3>
            <p className="text-gray-700 mb-3">
              Now that you've calculated your interest costs, use the comparison tool below to see how different interest rates can affect your loan. 
              Even a small difference in interest rate can save you thousands over the life of your loan.
            </p>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
              <span className="text-blue-700 font-medium">Scroll down to compare different interest rates</span>
            </div>
          </div>
        )}

        {/* Compare Interest Rates Calculator */}
        <Calculator
          title="Car Loan Interest Comparison Tool"
          description="Compare different interest rates to find the most affordable car loan option and visualize the differences."
          icon={TrendingUp}
          result={compareResult ? `Best Rate: ${Math.min(...compareResult.comparisons.map(c => c.rate))}%` : undefined}
        >
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Loan Amount"
                value={compareLoanAmount}
                onChange={setCompareLoanAmount}
                unit="$"
                placeholder="e.g. 30000"
              />
              <Input
                label="Loan Term"
                value={compareLoanTerm}
                onChange={setCompareLoanTerm}
                unit="years"
                placeholder="e.g. 5"
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <Input
                label="Interest Rate 1"
                value={interestRate1}
                onChange={setInterestRate1}
                unit="%"
                placeholder="e.g. 4.99"
              />
              <Input
                label="Interest Rate 2"
                value={interestRate2}
                onChange={setInterestRate2}
                unit="%"
                placeholder="e.g. 5.49"
              />
              <Input
                label="Interest Rate 3"
                value={interestRate3}
                onChange={setInterestRate3}
                unit="%"
                placeholder="e.g. 5.99"
              />
            </div>

            <button
              onClick={compareInterestRates}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
            >
              Compare Interest Rates & Calculate Savings
            </button>

            {compareResult !== null && (
              <div className="mt-6 space-y-6">
                <div className="p-4 bg-blue-50 rounded-md">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Rate Comparison</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest Rate</th>
                          <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Payment</th>
                          <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Interest</th>
                          <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {compareResult.comparisons.map((item, index) => (
                          <tr key={index}>
                            <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.rate}%</td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">${item.monthlyPayment.toFixed(2)}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">${item.totalInterest.toFixed(2)}</td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">${item.totalCost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="h-80">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Total Interest Comparison</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getChartData()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => typeof value === 'number' ? `$${value.toFixed(2)}` : value} />
                      <Legend />
                      <Bar dataKey="interest" name="Total Interest" fill="#3b82f6">
                        {getChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : index === 1 ? '#60a5fa' : '#93c5fd'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Calculator>
        
        <div className="bg-white rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Understanding Car Loan Interest in Australia</h2>
          <div className="space-y-6 text-gray-600">
            <p>
              Car loan interest is calculated based on the loan amount, interest rate, and loan term. The interest rate is one of the most important factors affecting your total loan cost in Australia.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-700">How Car Loan Interest is Calculated in Australia</h3>
            <p>
              Most Australian car loans use a reducing balance method, which means interest is calculated on the remaining balance of your loan, not the original amount. As you pay down the principal, you'll pay less interest over time.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-700">Current Car Loan Interest Rates in Australia (2025)</h3>
            <p>
              As of 2025, car loan interest rates in Australia typically range as follows:
            </p>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">🚗</span>Secured Car Loans
              </h4>
              <p className="mb-2">
                Secured car loans, where the vehicle serves as collateral, typically offer lower interest rates.
              </p>
              <p className="font-medium mb-1">Interest Rate Range: 5.49% - 10.49% p.a.</p>
              <p className="text-sm text-gray-600 mb-2">
                Example: Commonwealth Bank offers secured personal loans starting at 6.49% p.a., with a comparison rate of 7.90% p.a.
              </p>
              <div className="text-xs text-gray-500">Sources: Money.com.au, CommBank, Savings.com.au</div>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">🔒</span>Unsecured Car Loans
              </h4>
              <p className="mb-2">
                Unsecured car loans, which don't require collateral, generally have higher interest rates due to increased risk for lenders.
              </p>
              <p className="font-medium mb-1">Interest Rate Range: 5.76% - 24.03% p.a.</p>
              <p className="text-sm text-gray-600 mb-2">
                Example: Harmoney offers unsecured car loans starting at 5.76% p.a., with rates varying based on creditworthiness.
              </p>
              <div className="text-xs text-gray-500">Sources: Forbes, Canstar, Auto Car Loans, RateCity.com.au</div>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">🌱</span>Green Car Loans
              </h4>
              <p className="mb-2">
                Green car loans are designed for environmentally friendly vehicles, such as electric or hybrid cars, and often come with discounted rates.
              </p>
              <p className="font-medium mb-1">Interest Rate Range: 5.49% - 7.99% p.a.</p>
              <p className="text-sm text-gray-600 mb-2">
                Example: Commonwealth Bank offers a special rate of 5.49% p.a. (comparison rate 6.92% p.a.) for eligible electric or hybrid vehicles under its EV Access Program.
              </p>
              <div className="text-xs text-gray-500">Sources: Canstar, Savings.com.au, Auto Car Loans, CommBank</div>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">🚘</span>Novated Leases
              </h4>
              <p className="mb-2">
                Novated leases involve a three-way agreement between an employer, employee, and finance company, allowing for vehicle financing through salary packaging.
              </p>
              <p className="font-medium mb-1">Interest Rate Range: 5.99% - 8.99% p.a.</p>
              <p className="text-sm text-gray-600 mb-2">
                Note: Rates can vary based on the employer's agreement and the leasing company.
              </p>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">📊</span>Average Car Loan Interest Rates
              </h4>
              <p className="mb-1">
                According to recent data, the average fixed-term personal loan rate, which includes car loans, is approximately 9.68% p.a.
              </p>
              <div className="text-xs text-gray-500">Source: Money.com.au</div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-700">Factors Affecting Your Car Loan Interest Rate</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Credit score:</strong> Higher credit scores typically qualify for lower interest rates</li>
                  <li><strong>Loan term:</strong> Shorter terms often come with lower interest rates</li>
                  <li><strong>Vehicle age:</strong> Newer vehicles generally qualify for better rates</li>
                </ul>
              </div>
              <div>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Loan-to-value ratio:</strong> Larger down payments can lead to better rates</li>
                  <li><strong>Secured vs. unsecured:</strong> Secured loans offer lower rates</li>
                  <li><strong>Lender type:</strong> Banks, credit unions, and online lenders offer varying rates</li>
                </ul>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-700">Tips to Reduce Car Loan Interest in Australia</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Make additional repayments</strong> when possible - most Australian car loans allow extra payments</li>
              <li><strong>Shop around for the best interest rate</strong> - compare offers from different lenders</li>
              <li><strong>Consider a shorter loan term</strong> - while monthly payments will be higher, you'll pay less total interest</li>
              <li><strong>Improve your credit score</strong> before applying for a car loan</li>
              <li><strong>Consider refinancing</strong> if interest rates drop or your credit score improves</li>
              <li><strong>Choose a secured loan</strong> using the vehicle as collateral for lower rates</li>
              <li><strong>Look into dealer financing specials</strong> which may offer promotional rates</li>
              <li><strong>Explore green car loans</strong> if purchasing an electric or hybrid vehicle</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-700">Understanding Comparison Rates</h3>
            <p>
              In Australia, lenders must advertise a comparison rate alongside their headline interest rate. The comparison rate includes most fees and charges, giving you a more accurate picture of the total cost. Always compare car loans using the comparison rate rather than just the advertised rate.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-md mt-4">
              <h3 className="text-md font-semibold text-blue-800 mb-2">How This Calculator Helps</h3>
              <p className="text-blue-700">
                Our comprehensive car loan interest calculator lets you see exactly how different interest rates and extra payments affect your car loan in Australia. Use it to make informed decisions about your car financing and potentially save thousands over the life of your loan.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarLoanInterestCalculator; 